/**
 * Konkord: minesweeper rules. Pure functions, no screen and no network: the
 * player running the game applies them and sends the result to the call.
 *
 * Mines are placed on the first click, away from it, and the board is redrawn
 * until it can be solved without guessing (or the time budget runs out).
 */

export const MINE = 1;
export const OPEN = 2;
export const FLAG = 4;
/** "?": not sure yet (doesn't block opening) */
export const MARK = 8;

export const DIFFICULTIES = {
  easy: { width: 9, height: 9, mines: 10 },
  medium: { width: 16, height: 16, mines: 40 },
  hard: { width: 30, height: 16, mines: 99 },
} as const;

export type Difficulty = keyof typeof DIFFICULTIES;

export type MinesweeperStatus = "ready" | "playing" | "won" | "lost";

export type Minesweeper = {
  difficulty: Difficulty;
  width: number;
  height: number;
  mines: number;
  /** MINE | OPEN | FLAG | MARK bits per cell, row by row */
  cells: number[];
  status: MinesweeperStatus;
  /** the mine that went off */
  exploded: number;
  /** play time before the running stretch (pauses don't count) */
  elapsed: number;
  /** when the running stretch started, undefined while stopped */
  since?: number;
};

export type MinesweeperMove = {
  action: "open" | "flag" | "chord";
  cell: number;
};

/** Generation gives up looking for a board without guesses after this long */
const GENERATE_BUDGET_MS = 300;

/**
 * Seeded random numbers (mulberry32): the same seed always gives the same board
 */
export function random(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function newMinesweeper(difficulty: Difficulty): Minesweeper {
  const { width, height, mines } = DIFFICULTIES[difficulty];
  return {
    difficulty,
    width,
    height,
    mines,
    cells: new Array(width * height).fill(0),
    status: "ready",
    exploded: -1,
    elapsed: 0,
  };
}

/**
 * Cells around a cell (up to 8)
 */
export function neighbours(width: number, height: number, cell: number) {
  const x = cell % width;
  const y = Math.floor(cell / width);
  const result: number[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      if (!dx && !dy) continue;
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && ny >= 0 && nx < width && ny < height) {
        result.push(ny * width + nx);
      }
    }
  }
  return result;
}

/**
 * How many mines touch each cell
 */
export function counts(game: Pick<Minesweeper, "width" | "height" | "cells">) {
  return game.cells.map((_, cell) =>
    neighbours(game.width, game.height, cell).reduce(
      (sum, other) => sum + (game.cells[other] & MINE),
      0,
    ),
  );
}

export function flagsPlaced(game: Minesweeper) {
  return game.cells.reduce((sum, cell) => sum + (cell & FLAG ? 1 : 0), 0);
}

/**
 * Play time in ms (a running game counts up to now)
 */
export function playTime(game: Minesweeper, now = Date.now()) {
  // a clock a moment behind the game's start counts zero, not less
  return game.elapsed + (game.since === undefined ? 0 : Math.max(0, now - game.since));
}

/**
 * Stop or restart the clock (pausing doesn't count as play time)
 */
export function setRunning(
  game: Minesweeper,
  running: boolean,
  now = Date.now(),
): Minesweeper {
  if (game.status !== "playing") return game;
  if (running === (game.since !== undefined)) return game;
  return running
    ? { ...game, since: now }
    : { ...game, elapsed: playTime(game, now), since: undefined };
}

function placeMines(
  width: number,
  height: number,
  mines: number,
  start: number,
  next: () => number,
) {
  const keepClear = new Set([start, ...neighbours(width, height, start)]);
  // tiny boards can't keep the whole 3x3 clear: only the clicked cell then
  const candidates: number[] = [];
  for (let cell = 0; cell < width * height; cell++) {
    if (!keepClear.has(cell)) candidates.push(cell);
  }
  if (candidates.length < mines) {
    candidates.length = 0;
    for (let cell = 0; cell < width * height; cell++) {
      if (cell !== start) candidates.push(cell);
    }
  }

  // partial Fisher-Yates
  for (let i = 0; i < mines; i++) {
    const j = i + Math.floor(next() * (candidates.length - i));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }
  const cells = new Array(width * height).fill(0);
  for (let i = 0; i < mines; i++) cells[candidates[i]] = MINE;
  return cells;
}

/**
 * Whether a board can be cleared from the start cell by logic alone (single
 * cell rules, one constraint inside another, and the mine count)
 */
export function solvable(
  width: number,
  height: number,
  cells: number[],
  start: number,
) {
  const total = width * height;
  const mineCount = cells.reduce((sum, cell) => sum + (cell & MINE), 0);
  const number = counts({ width, height, cells });
  const open = new Uint8Array(total);
  const mine = new Uint8Array(total);
  let opened = 0;
  let marked = 0;

  const openCell = (first: number) => {
    const stack = [first];
    while (stack.length) {
      const cell = stack.pop()!;
      if (open[cell] || mine[cell]) continue;
      if (cells[cell] & MINE) return false;
      open[cell] = 1;
      opened++;
      if (number[cell] === 0) stack.push(...neighbours(width, height, cell));
    }
    return true;
  };

  if (!openCell(start)) return false;

  while (opened < total - mineCount) {
    let progress = false;

    // constraints: unknown neighbours of an open number and mines still missing
    const constraints: { cell: number; unknown: number[]; left: number }[] = [];
    for (let cell = 0; cell < total; cell++) {
      if (!open[cell] || number[cell] === 0) continue;
      const around = neighbours(width, height, cell);
      const unknown = around.filter((other) => !open[other] && !mine[other]);
      if (!unknown.length) continue;
      const left =
        number[cell] - around.filter((other) => mine[other]).length;
      constraints.push({ cell, unknown, left });
    }

    const settle = (unknown: number[], asMine: boolean) => {
      for (const cell of unknown) {
        if (open[cell] || mine[cell]) continue;
        if (asMine) {
          mine[cell] = 1;
          marked++;
        } else if (!openCell(cell)) {
          throw new Error("solver opened a mine");
        }
        progress = true;
      }
    };

    for (const { unknown, left } of constraints) {
      if (left === 0) settle(unknown, false);
      else if (left === unknown.length) settle(unknown, true);
    }

    if (!progress) {
      // one constraint inside another: the difference holds the rest
      outer: for (const a of constraints) {
        const inA = new Set(a.unknown);
        for (const b of constraints) {
          if (a === b || b.unknown.length <= a.unknown.length) continue;
          if (!a.unknown.every((cell) => b.unknown.includes(cell))) continue;
          const rest = b.unknown.filter((cell) => !inA.has(cell));
          const left = b.left - a.left;
          if (left === 0) settle(rest, false);
          else if (left === rest.length) settle(rest, true);
          if (progress) break outer;
        }
      }
    }

    if (!progress) {
      // the mine count settles the cells nobody touches
      const unknown: number[] = [];
      for (let cell = 0; cell < total; cell++) {
        if (!open[cell] && !mine[cell]) unknown.push(cell);
      }
      if (marked === mineCount) settle(unknown, false);
      else if (mineCount - marked === unknown.length) settle(unknown, true);
    }

    if (!progress) return false;
  }
  return true;
}

/**
 * Mines for a first click on `start`: without guesses when one turns up in time
 */
export function generate(
  width: number,
  height: number,
  mines: number,
  start: number,
  seed: number,
  budgetMs = GENERATE_BUDGET_MS,
) {
  const next = random(seed);
  const until = Date.now() + budgetMs;
  let cells = placeMines(width, height, mines, start, next);
  while (!solvable(width, height, cells, start) && Date.now() < until) {
    cells = placeMines(width, height, mines, start, next);
  }
  return cells;
}

function won(cells: number[], mines: number) {
  let open = 0;
  for (const cell of cells) if (cell & OPEN) open++;
  return open === cells.length - mines;
}

function finish(game: Minesweeper, status: "won" | "lost", now: number) {
  const stopped = { ...game, elapsed: playTime(game, now), since: undefined };
  if (status === "won") {
    // every mine gets its flag
    stopped.cells = stopped.cells.map((cell) =>
      cell & MINE ? (cell | FLAG) & ~OPEN & ~MARK : cell,
    );
  }
  return { ...stopped, status };
}

function openFrom(game: Minesweeper, first: number, now: number): Minesweeper {
  const cells = game.cells.slice();
  const number = counts(game);
  const stack = [first];
  while (stack.length) {
    const cell = stack.pop()!;
    if (cells[cell] & (OPEN | FLAG)) continue;
    if (cells[cell] & MINE) {
      return finish({ ...game, cells, exploded: cell }, "lost", now);
    }
    cells[cell] = (cells[cell] | OPEN) & ~MARK;
    if (number[cell] === 0) {
      stack.push(...neighbours(game.width, game.height, cell));
    }
  }
  const next = { ...game, cells };
  return won(cells, game.mines) ? finish(next, "won", now) : next;
}

/**
 * Apply a move; returns the same object when nothing changes
 */
export function play(
  game: Minesweeper,
  move: MinesweeperMove,
  seed: number,
  now = Date.now(),
): Minesweeper {
  const { cell } = move;
  if (
    game.status === "won" ||
    game.status === "lost" ||
    !Number.isInteger(cell) ||
    cell < 0 ||
    cell >= game.cells.length
  ) {
    return game;
  }

  // right click: flag, then "?", then nothing
  if (move.action === "flag") {
    const value = game.cells[cell];
    if (value & OPEN) return game;
    const cells = game.cells.slice();
    cells[cell] =
      value & FLAG
        ? (value & ~FLAG) | MARK
        : value & MARK
          ? value & ~MARK
          : value | FLAG;
    return { ...game, cells };
  }

  if (move.action === "open") {
    if (game.cells[cell] & (OPEN | FLAG)) return game;
    let current = game;
    if (game.status === "ready") {
      const mines = generate(game.width, game.height, game.mines, cell, seed);
      current = {
        ...game,
        // flags and marks placed before the first click stay
        cells: mines.map((mine, i) => mine | (game.cells[i] & (FLAG | MARK))),
        status: "playing",
        since: now,
      };
    }
    return openFrom(current, cell, now);
  }

  // chord: an open number with all its flags placed opens the rest around it
  if (!(game.cells[cell] & OPEN)) return game;
  const around = neighbours(game.width, game.height, cell);
  const flags = around.filter((other) => game.cells[other] & FLAG).length;
  if (flags !== counts(game)[cell]) return game;
  let current = game;
  for (const other of around) {
    if (current.status !== "playing") break;
    if (!(current.cells[other] & (OPEN | FLAG))) {
      current = openFrom(current, other, now);
    }
  }
  return current;
}
